import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { X, BookOpen, Loader2, ChevronDown, ChevronUp, CheckCircle, XCircle } from 'lucide-react'
import { Progress } from '../ui/progress'
import { Button } from '../ui/button'
import { apiClient } from '../../lib/api'
import { getLanguageByCode } from '../../lib/constants/languages'
import { useAuth } from '../../contexts/AuthContext'
import { notifyTranslationComplete, translationEvents } from '../../lib/events/translation-events'

interface ActiveTranslation {
  job_id: number
  translation_id: string
  book_id: string
  book_title: string | null
  target_language: string
  cefr_level: string
  status: 'queued' | 'running'
  current_batch: number
  total_batches: number
  progress_percent: number
  created_at: string
  updated_at: string
}

interface CompletedTranslation extends ActiveTranslation {
  completedAt: number
}

interface FailedTranslation extends ActiveTranslation {
  failedAt: number
  error_message?: string
}

export function TranslationProgressWidget() {
  const { t } = useTranslation()
  const { user } = useAuth()
  const navigate = useNavigate()
  const [translations, setTranslations] = useState<ActiveTranslation[]>([])
  const [completedTranslations, setCompletedTranslations] = useState<CompletedTranslation[]>([])
  const [failedTranslations, setFailedTranslations] = useState<FailedTranslation[]>([])
  const [dismissed, setDismissed] = useState<Set<number>>(new Set())
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 640)
  
  // Start minimized on mobile (< 640px)
  const getInitialMinimizedState = useCallback(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth < 640
    }
    return false
  }, [])
  const [isMinimized, setIsMinimized] = useState(getInitialMinimizedState)

  // Track mobile state on resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 640)
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])
  
  // Use refs to track state without causing re-renders
  const previousJobIdsRef = useRef<Set<number>>(new Set())
  const translationsRef = useRef<ActiveTranslation[]>([])
  const dismissedRef = useRef<Set<number>>(new Set())

  // Keep refs in sync with state
  useEffect(() => {
    translationsRef.current = translations
  }, [translations])

  useEffect(() => {
    dismissedRef.current = dismissed
  }, [dismissed])

  // Clear completed translations after 5 seconds
  useEffect(() => {
    if (completedTranslations.length === 0) return
    
    const timer = setInterval(() => {
      const now = Date.now()
      setCompletedTranslations(prev => 
        prev.filter(t => now - t.completedAt < 5000)
      )
    }, 1000)
    
    return () => clearInterval(timer)
  }, [completedTranslations.length])

  // Track if polling is active
  const pollingTimerRef = useRef<NodeJS.Timeout | null>(null)
  const isPollingRef = useRef(false)

  // Main polling effect - runs once on mount, then sets up interval only if there are active translations
  useEffect(() => {
    if (!user) return

    const fetchActiveTranslations = async (): Promise<number> => {
      try {
        const data = await apiClient.getActiveTranslations()
        const currentDismissed = dismissedRef.current
        const activeTranslations = data.translations.filter(t => !currentDismissed.has(t.job_id))
        
        // Check for jobs that disappeared from active list
        const currentJobIds = new Set(activeTranslations.map(t => t.job_id))
        const previousJobIds = previousJobIdsRef.current
        const currentTranslations = translationsRef.current
        
        // Check each disappeared job to see if it succeeded or failed
        for (const jobId of previousJobIds) {
          if (!currentJobIds.has(jobId) && !currentDismissed.has(jobId)) {
            const disappearedJob = currentTranslations.find(t => t.job_id === jobId)
            if (disappearedJob) {
              try {
                // Verify the job status
                const jobStatus = await apiClient.getJobStatus(jobId)
                if (jobStatus.job?.status === 'succeeded') {
                  setCompletedTranslations(prev => [
                    ...prev,
                    { ...disappearedJob, progress_percent: 100, completedAt: Date.now() }
                  ])
                  // Notify book detail page to refresh
                  notifyTranslationComplete(disappearedJob.book_id, disappearedJob.translation_id, 'completed')
                } else if (jobStatus.job?.status === 'failed') {
                  setFailedTranslations(prev => [
                    ...prev,
                    { ...disappearedJob, failedAt: Date.now(), error_message: jobStatus.job?.last_error ?? undefined }
                  ])
                  // Notify book detail page to refresh
                  notifyTranslationComplete(disappearedJob.book_id, disappearedJob.translation_id, 'failed')
                }
              } catch {
                // If we can't get status, don't show anything
                console.debug(`Could not verify job ${jobId} status`)
              }
            }
          }
        }
        
        setTranslations(activeTranslations)
        previousJobIdsRef.current = currentJobIds
        return activeTranslations.length
      } catch (error) {
        console.debug('Failed to fetch translations:', error)
        return 0
      }
    }

    const startPolling = () => {
      if (isPollingRef.current) return
      isPollingRef.current = true
      pollingTimerRef.current = setInterval(async () => {
        const count = await fetchActiveTranslations()
        // Stop polling if there are no active translations
        if (count === 0) {
          stopPolling()
        }
      }, 5000)
    }

    const stopPolling = () => {
      if (pollingTimerRef.current) {
        clearInterval(pollingTimerRef.current)
        pollingTimerRef.current = null
      }
      isPollingRef.current = false
    }

    // Initial fetch - start polling only if there are active translations
    fetchActiveTranslations().then(count => {
      if (count > 0) {
        startPolling()
      }
    })

    // Subscribe to translation started events to resume polling
    const unsubscribe = translationEvents.subscribeToStarted(() => {
      // When a translation is started, do an immediate fetch and start polling
      fetchActiveTranslations().then(count => {
        if (count > 0) {
          startPolling()
        }
      })
    })
    
    return () => {
      stopPolling()
      unsubscribe()
    }
  }, [user]) // Only depends on user

  const handleDismiss = (jobId: number) => {
    setDismissed(prev => new Set([...prev, jobId]))
  }

  const handleDismissAll = () => {
    const allJobIds = [...translations, ...completedTranslations, ...failedTranslations].map(t => t.job_id)
    setDismissed(prev => new Set([...prev, ...allJobIds]))
    setCompletedTranslations([])
    setFailedTranslations([])
  }

  const handleDismissFailed = (jobId: number) => {
    setFailedTranslations(prev => prev.filter(t => t.job_id !== jobId))
    setDismissed(prev => new Set([...prev, jobId]))
  }

  const handleViewBook = (bookId: string) => {
    navigate(`/user/books/${bookId}`)
  }

  const getLanguageName = (code: string): string => {
    const lang = getLanguageByCode(code)
    return lang ? lang.name : code
  }

  const allItems = [...completedTranslations, ...failedTranslations, ...translations]
  const shouldShow = !!user && allItems.length > 0
  
  if (!shouldShow) return null

  // Mobile minimized: show animated floating book icon
  if (isMobile && isMinimized) {
    const hasFailures = failedTranslations.length > 0
    const hasCompleted = completedTranslations.length > 0
    
    return (
      <div className="translation-progress-widget-mobile">
        <button
          onClick={() => setIsMinimized(false)}
          className="mobile-icon-btn group relative w-14 h-14 rounded-full bg-gradient-to-br from-primary to-primary/80 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 flex items-center justify-center"
          aria-label={t('translation.showTranslations', 'Show translations')}
        >
          {/* Animated book icon */}
          <div className="book-animation relative">
            <BookOpen className="h-6 w-6 text-white animate-pulse" />
            {/* Sparkle/creation effect */}
            <span className="absolute -top-1 -right-1 w-2 h-2 bg-white rounded-full animate-ping opacity-75" />
            <span className="absolute -bottom-0.5 -left-0.5 w-1.5 h-1.5 bg-white/80 rounded-full animate-ping opacity-60" style={{ animationDelay: '0.3s' }} />
          </div>
          
          {/* Badge with count */}
          <span className={`absolute -top-1 -right-1 min-w-5 h-5 flex items-center justify-center text-xs font-bold rounded-full px-1.5 ${
            hasFailures 
              ? 'bg-red-500 text-white' 
              : hasCompleted 
                ? 'bg-green-500 text-white' 
                : 'bg-white text-primary'
          }`}>
            {allItems.length}
          </span>
          
          {/* Rotating ring effect */}
          <span className="absolute inset-0 rounded-full border-2 border-white/30 animate-spin" style={{ animationDuration: '3s' }} />
        </button>
      </div>
    )
  }

  return (
    <div className="translation-progress-widget w-64 sm:w-80">
      <div className="widget-card rounded-lg bg-white dark:bg-gray-800 shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        {/* Header */}
        <div className="widget-header flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <BookOpen className="h-4 w-4 text-primary" />
            <span className="text-sm font-medium">
              {t('translation.translationsInProgress', 'Translations')}
            </span>
            <span className="bg-primary/10 text-primary text-xs px-1.5 py-0.5 rounded-full">
              {allItems.length}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <button 
              onClick={() => setIsMinimized(!isMinimized)}
              className="minimize-btn p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
              aria-label={isMinimized ? 'Expand' : 'Minimize'}
            >
              {isMinimized ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>
            <button 
              onClick={handleDismissAll}
              className="close-btn p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
              aria-label="Dismiss all"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Content */}
        {!isMinimized && (
          <div className="widget-content p-3 max-h-64 overflow-y-auto space-y-3">
            {/* Completed translations (briefly shown) */}
            {completedTranslations.map(item => (
              <div 
                key={`completed-${item.job_id}`} 
                className="translation-item p-2 rounded-md bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 animate-pulse"
              >
                <div className="flex items-center justify-between gap-2 mb-1">
                  <span className="text-sm font-medium truncate flex-1" title={item.book_title || ''}>
                    {item.book_title || t('common.untitled', 'Untitled')}
                  </span>
                  <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                </div>
                <div className="flex items-center justify-between text-xs text-green-700 dark:text-green-400">
                  <span>{getLanguageName(item.target_language)} • {item.cefr_level}</span>
                  <span>{t('translation.completed', 'Complete!')}</span>
                </div>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="w-full mt-2 h-7 text-xs"
                  onClick={() => handleViewBook(item.book_id)}
                >
                  {t('translation.viewBook', 'View Book')}
                </Button>
              </div>
            ))}

            {/* Failed translations (persist until dismissed) */}
            {failedTranslations.map(item => (
              <div 
                key={`failed-${item.job_id}`} 
                className="translation-item p-2 rounded-md bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800"
              >
                <div className="flex items-center justify-between gap-2 mb-1">
                  <span className="text-sm font-medium truncate flex-1" title={item.book_title || ''}>
                    {item.book_title || t('common.untitled', 'Untitled')}
                  </span>
                  <button 
                    onClick={() => handleDismissFailed(item.job_id)}
                    className="dismiss-item-btn p-0.5 hover:bg-red-200 dark:hover:bg-red-800 rounded"
                    aria-label="Dismiss"
                  >
                    <X className="h-3 w-3 text-red-400" />
                  </button>
                </div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs text-muted-foreground">
                    {getLanguageName(item.target_language)} • {item.cefr_level}
                  </span>
                </div>
                <div className="flex items-center gap-1 text-xs text-red-600 dark:text-red-400">
                  <XCircle className="h-3 w-3 flex-shrink-0" />
                  <span className="truncate" title={item.error_message}>
                    {t('translation.failed', 'Translation failed')}
                  </span>
                </div>                
              </div>
            ))}

            {/* Active translations */}
            {translations.map(trans => (
              <div key={trans.job_id} className="translation-item p-2 rounded-md bg-gray-50 dark:bg-gray-900">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <span className="text-sm font-medium truncate flex-1" title={trans.book_title || ''}>
                    {trans.book_title || t('common.untitled', 'Untitled')}
                  </span>
                  <button 
                    onClick={() => handleDismiss(trans.job_id)}
                    className="dismiss-item-btn p-0.5 hover:bg-gray-200 dark:hover:bg-gray-700 rounded"
                    aria-label="Dismiss"
                  >
                    <X className="h-3 w-3 text-gray-400" />
                  </button>
                </div>
                
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs text-muted-foreground">
                    {getLanguageName(trans.target_language)} • {trans.cefr_level}
                  </span>
                </div>
                
                <Progress value={trans.progress_percent} className="h-1.5 mb-1" />
                
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    {trans.status === 'queued' && t('translation.waitingInQueue', 'In queue...')}
                    {trans.status === 'running' && t('translation.translating', 'Translating...')}
                  </span>
                  <span>{trans.progress_percent}%</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

